import maplibregl from 'maplibre-gl'
import drawVertexSource from './shaders/draw.vert.glsl'
import screenVertexSource from './shaders/screen.vert.glsl'
import updateVertexSource from './shaders/update.vert.glsl'
import drawFragmentSource from './shaders/draw.frag.glsl'
import screenFragmentSource from './shaders/screen.frag.glsl'
import updateFragmentSource from './shaders/update.frag.glsl'
import { getSmartPlatform } from '@weathernews/btos-javascript-modules/src/wniutil/common.js'

const isMobile = import.meta.client ? getSmartPlatform() !== '' : false

export class WindFlowLayer {
  constructor({ id, data, GRID_INFO, opacity = 1.0, spec, particleSize = 1.0, particleType, speedFactor, dropRateBump = 0.0001, dropRate = 0.005, fadeOpacity = 0.7, maxParticles = 3000 }) {
    const projFunc
      = maplibregl.MercatorCoordinate.fromLngLat
    this.id = id
    this.windData = data
    this.specUrl = spec
    this.GRID_INFO = structuredClone(GRID_INFO)
    this.opacity = opacity
    this.type = 'custom'
    this.renderingMode = '3d'
    this.antialias = true
    this.GRID_INFO.LAT_DIFF
      = (GRID_INFO.NLAT - GRID_INFO.SLAT) / GRID_INFO.IMGH
    this.GRID_INFO.LON_DIFF
      = (GRID_INFO.ELON - GRID_INFO.WLON) / GRID_INFO.IMGW
    const mercEN = projFunc([GRID_INFO.ELON, GRID_INFO.NLAT])
    this.GRID_INFO.E_MERC_X = mercEN.x
    this.GRID_INFO.N_MERC_Y = mercEN.y
    const mercWS = projFunc([GRID_INFO.WLON, GRID_INFO.SLAT])
    this.GRID_INFO.W_MERC_X = mercWS.x
    this.GRID_INFO.S_MERC_Y = mercWS.y
    this.defaultParticleSize = particleSize
    this.maxParticles = maxParticles

    this.fadeOpacity = fadeOpacity // how fast the particle trails fade on each frame
    this.dropRate = dropRate // how often the particles move to a random place
    this.dropRateBump = dropRateBump // drop rate increase relative to individual particle speed
    this.particleType = particleType

    this.dpr = window.devicePixelRatio || 1
    this.speedFactor = (speedFactor || 0.70) * Math.sqrt(this.dpr) // how fast the particles move
    this.matrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1
    ])
  }

  loadImage(imgUrl) {
    return new Promise((resolve) => {
      const img = new Image()
      img.addEventListener('load', (e) => {
        resolve(e.target)
      })
      img.src = imgUrl
    })
  }

  async loadShaders(gl) {
    return Promise.all([
      glUtil.createVertexShader(gl, drawVertexSource),
      glUtil.createVertexShader(gl, screenVertexSource),
      glUtil.createVertexShader(gl, updateVertexSource),
      glUtil.createFragmentShader(gl, drawFragmentSource),
      glUtil.createFragmentShader(gl, screenFragmentSource),
      glUtil.createFragmentShader(gl, updateFragmentSource)
    ])
  }

  async initProgram(gl) {
    // https://app.diagrams.net/#G1koDnTzo5ahu-w8PDRPGUGDs0xnYu_uXy
    const [
      drawVertexShader,
      screenVertexShader,
      updateVertexShader,
      drawFlagmentShader,
      screenFlagmentShader,
      updateFlagmentShader
    ] = await this.loadShaders(gl)

    // パーティクルの描画
    this.drawProgram = glUtil.createProgram(
      gl,
      drawVertexShader,
      drawFlagmentShader
    )
    // テクスチャに描画
    this.screenProgram = glUtil.createProgram(
      gl,
      screenVertexShader,
      screenFlagmentShader
    )
    // パーティクルの移動
    this.updateProgram = glUtil.createProgramFeedback(
      gl,
      updateVertexShader,
      updateFlagmentShader,
      ['gl_Position']
    )

    // 板ポリの頂点バッファを作成する
    this.quadBuffer = glUtil.createVBO(
      gl,
      [0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 1.0, 1.0, 0.0, 1.0, 1.0],
      2
    )

    // パーティクルのサイズを調整
    const shapeArray = this.particleType === 'arrow'
      ? [
        [-1.0, 0.0],
        [0.0, -0.25],
        [0.0, -0.6],
        [0.0, -0.25],
        [1.0, 0.0],
        [0.0, -0.6]
      ].flat().map((p) => p / 100 * this.dpr * this.defaultParticleSize)
      : [
        [-0.025, 0.05],
        [-0.025, -0.05],
        [0.025, -0.05],
        [-0.025, 0.05],
        [0.025, -0.05],
        [0.025, 0.05]
      ].flat().map((p) => p / 30 * this.defaultParticleSize) // OneBox風レーダー用の調整

    this.shapeBuffer = glUtil.createVBO(
      gl,
      shapeArray,
      2
    )

    // 出力用板ポリの頂点バッファを作成する
    const outputPath = [
      this.GRID_INFO.W_MERC_X,
      this.GRID_INFO.N_MERC_Y,
      this.GRID_INFO.E_MERC_X,
      this.GRID_INFO.N_MERC_Y,
      this.GRID_INFO.W_MERC_X,
      this.GRID_INFO.S_MERC_Y,
      this.GRID_INFO.W_MERC_X,
      this.GRID_INFO.S_MERC_Y,
      this.GRID_INFO.E_MERC_X,
      this.GRID_INFO.N_MERC_Y,
      this.GRID_INFO.E_MERC_X,
      this.GRID_INFO.S_MERC_Y
    ]
    this.outputBuffer = glUtil.createVBO(gl, outputPath, 2)

    // フレームバッファの作成
    this.framebuffer = gl.createFramebuffer()

    this.init = true
  }

  resetCanvasSize() {
    const gl = this.gl

    gl.canvas.width = this.map.getCanvas().width
    gl.canvas.height = this.map.getCanvas().height
    // gl.canvas.style.width = (gl.canvas.width / this.dpr) + 'px'
    // gl.canvas.style.height = (gl.canvas.height / this.dpr) + 'px'

    this.displayAspectRatio = gl.canvas.height / gl.canvas.width
  }

  resize() {
    const gl = this.gl

    this.resetCanvasSize()

    // 解放処理を追加：前回生成したテクスチャ等があればクリーンアップ
    if (this.backgroundTexture) {
      this.gl.deleteTexture(this.backgroundTexture)
      this.backgroundTexture = null
    }
    if (this.screenTexture) {
      this.gl.deleteTexture(this.screenTexture)
      this.screenTexture = null
    }
    if (!this.framebuffer) {
      this.framebuffer = gl.createFramebuffer()
    }

    const emptyPixels = new Uint8Array(gl.canvas.width * gl.canvas.height * 4)
    // screen textures to hold the drawn screen for the previous and the current frame
    this.backgroundTexture = glUtil.createTexture(
      gl,
      emptyPixels,
      gl.canvas.width,
      gl.canvas.height,
      gl.NEAREST
    )
    this.screenTexture = glUtil.createTexture(
      gl,
      emptyPixels,
      gl.canvas.width,
      gl.canvas.height,
      gl.NEAREST
    )

    // キャンバス内のどこに描画するかを計算する(0-1の割合)
    const nw = this.map.project([this.GRID_INFO.WLON, this.GRID_INFO.NLAT])
    const se = this.map.project([this.GRID_INFO.ELON, this.GRID_INFO.SLAT])
    this.bbox = [
      nw.x / (gl.canvas.width / this.dpr),
      nw.y / (gl.canvas.height / this.dpr),
      se.x / (gl.canvas.width / this.dpr),
      se.y / (gl.canvas.height / this.dpr)
    ]

    // this.particleSize = Math.max(1.2, this.map.getZoom() ** 1.5 / 40) * dpr;
    // this.particleSize = (this.defaultParticleSize ?? 2) * this.dpr
    // this.particleSize = Math.max(1.6, Math.min(15 / this.map.getZoom(), 1.7)) * dpr;
    this.createParticleTexture(this.getPartcleNumber())
  }

  /**
   * パーティクルをクリアする関数
   */
  clear() {
    // this.createParticleTexture(0);
    const gl = this.gl
    gl.clearColor(0.0, 0.0, 0.0, 0.0)

    if (this.framebuffer) {
      // clear framebuffer textures
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.screenTexture, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.backgroundTexture, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)

      // target normal canvas
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    }
    // clear canvas
    gl.clear(gl.COLOR_BUFFER_BIT)
  }

  anim() {
    this.clearId = requestAnimationFrame(this.anim.bind(this))
    this.draw(this.gl)
  }

  /**
   * ランダムなパーティクルを生成し、テクスチャにセットする関数
   * @param {Number} numParticles パーティクルの数
   */
  createParticleTexture(numParticles) {
    const gl = this.gl
    if (!gl || !gl.canvas || numParticles <= 0 || gl.canvas.width === 0 || gl.canvas.height === 0) return

    // 正方形のテクスチャを作成する
    const particleRes = (this.particleStateResolution = Math.ceil(
      Math.sqrt(numParticles)
    ))
    this.numParticles = particleRes * particleRes
    this.floatFramebuffer = glUtil.createFloatFramebuffer(gl, particleRes, particleRes)

    // 値を ランダムに初期化する（この値がパーティクルの位置として使われる）
    const particleState = new Float32Array(this.numParticles * 4)
    for (let i = 0; i < particleState.length; i += 4) {
      particleState[i + 0] = this.bbox[0] + Math.random() * (this.bbox[2] - this.bbox[0])
      particleState[i + 1] = this.bbox[1] + Math.random() * (this.bbox[3] - this.bbox[1])
      particleState[i + 2] = 0
      particleState[i + 3] = 0
    }

    this.particleStateBuffer0 = glUtil.createVBOFeedback(gl, particleState, 4)
    this.particleStateBuffer1 = glUtil.createVBOFeedback(gl, particleState, 4)
  }

  setWind(data) {
    this.windTexture = glUtil.createFloatTexture(
      this.gl,
      data.data,
      data.width,
      data.height
    )
  }

  async loadBinData(url, TypedArray = Float32Array) {
    const binData = await fetch(url).then((res) => res.arrayBuffer())
    return new TypedArray(
      binData,
      0,
      this.GRID_INFO.IMGW * this.GRID_INFO.IMGH
    )
  }

  /**
   * パーティクルの描画と更新を行う関数（ループ時に最初に呼ばれる箇所）
   * @param {WebGLRenderingContext} gl
   */
  draw(gl) {
    gl.disable(gl.DEPTH_TEST)
    gl.disable(gl.STENCIL_TEST)

    this.drawScreen()
    this.updateParticles()
  }

  /**
   * 最終的な描画
   */
  drawScreen() {
    const gl = this.gl

    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)
    // フレームバッファの更新
    {
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      glUtil.bindFramebuffer(gl, this.framebuffer, this.screenTexture)

      this.drawTexture(this.backgroundTexture, this.fadeOpacity)
      this.drawParticles()

      glUtil.bindFramebuffer(gl, null)
    }

    // 表示の更新
    // enable blending to support drawing on top of an existing background (e.g. a map)
    {
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      this.drawTexture(this.screenTexture, 1.0)
      gl.disable(gl.BLEND)
    }

    // テクスチャの入れ替え
    // save the current screen as the background for the next frame
    {
      const temp = this.backgroundTexture
      this.backgroundTexture = this.screenTexture
      this.screenTexture = temp
    }
  }

  drawParticles() {
    if (!this.particleStateBuffer0) return
    const gl = this.gl
    const program = this.drawProgram
    gl.useProgram(program.program)

    glUtil.bindAttribute(gl, this.shapeBuffer.buffer, program.a_shape, this.shapeBuffer.length)
    glUtil.bindAttribute(gl, this.particleStateBuffer0.buffer, program.a_particle_pos, this.particleStateBuffer0.length)
    glUtil.bindAttributeInstancedArray(gl, this.instancedExt, this.particleStateBuffer0.buffer, program.a_particle_pos, this.particleStateBuffer0.length)

    glUtil.bindTexture(gl, this.windTexture, 0)

    gl.uniform1i(program.u_wind, 0)
    gl.uniform1i(program.u_particles, 1)
    // gl.uniform1i(program.u_color_ramp, 2);

    gl.uniform1f(program.u_particles_res, this.particleStateResolution)
    gl.uniform1f(program.u_point_size, this.particleSize)
    // gl.uniform2f(program.u_wind_min, this.windData.uMin, this.windData.vMin);
    // gl.uniform2f(program.u_wind_max, this.windData.uMax, this.windData.vMax);
    gl.uniform2fv(program.u_x_range, [this.bbox[0], this.bbox[2]])
    gl.uniform2fv(program.u_y_range, [this.bbox[1], this.bbox[3]])

    // gl.drawArrays(gl.POINTS, 0, this.numParticles);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, this.numParticles)

    gl.vertexAttribDivisor(program.a_index, 0)
  }

  updateParticles() {
    if (!this.particleStateBuffer0 || !this.particleStateBuffer1) return
    const gl = this.gl
    glUtil.bindFramebuffer(gl, this.floatFramebuffer.framebuffer, this.particleStateTexture1)
    gl.viewport(
      0,
      0,
      this.particleStateResolution,
      this.particleStateResolution
    )
    const program = this.updateProgram
    gl.useProgram(program.program)

    glUtil.bindTexture(gl, this.windTexture, 0)
    glUtil.bindAttribute(gl, this.particleStateBuffer0.buffer, program.a_particle_pos, this.particleStateBuffer0.length)

    gl.uniform1i(program.u_wind, 0)

    gl.uniform1f(program.u_rand_seed, Math.random())
    gl.uniform2f(program.u_wind_res, this.windData.width, this.windData.height)
    // gl.uniform2f(program.u_wind_min, this.windData.uMin, this.windData.vMin);
    // gl.uniform2f(program.u_wind_max, this.windData.uMax, this.windData.vMax);
    gl.uniform1f(program.u_speed_factor, this.speedFactor * Math.sqrt(this.map.getZoom()))
    gl.uniform1f(program.u_drop_rate, this.dropRate)
    gl.uniform1f(program.u_drop_rate_bump, this.dropRateBump * Math.sqrt(this.map.getZoom()))
    gl.uniform1f(program.u_constant_speed, 1.0)

    gl.uniform2fv(program.u_x_range, [this.bbox[0], this.bbox[2]])
    gl.uniform2fv(program.u_y_range, [this.bbox[1], this.bbox[3]])
    gl.uniform1f(program.u_north_lat, this.GRID_INFO.N_LAT)
    gl.uniform1f(program.u_south_lat, this.GRID_INFO.S_LAT)
    gl.uniform1f(program.u_north_merc_y, this.GRID_INFO.N_MERC_Y)
    gl.uniform1f(program.u_south_merc_y, this.GRID_INFO.S_MERC_Y)

    gl.uniform1f(program.u_display_aspect_ratio, this.displayAspectRatio)
    gl.uniformMatrix4fv(program.u_matrix, false, this.matrix)

    gl.enable(gl.RASTERIZER_DISCARD)
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.particleStateBuffer1.buffer)
    gl.beginTransformFeedback(gl.POINTS)

    gl.drawArrays(gl.POINTS, 0, this.numParticles)

    gl.endTransformFeedback()
    gl.disable(gl.RASTERIZER_DISCARD)
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, null)

    // swap the particle state textures so the new one becomes the current one
    {
      const temp = this.particleStateBuffer0
      this.particleStateBuffer0 = this.particleStateBuffer1
      this.particleStateBuffer1 = temp
    }
    // glUtil.bindFramebuffer(gl, null);
  }

  drawTexture(texture, opacity) {
    const gl = this.gl

    const program = this.screenProgram
    gl.useProgram(program.program)

    glUtil.bindAttribute(gl, this.quadBuffer.buffer, program.a_pos, this.quadBuffer.length)
    glUtil.bindTexture(gl, texture, 2)
    gl.uniform1i(program.u_screen, 2)
    gl.uniform1f(program.u_opacity, opacity)

    if (!this.matrix) return
    gl.uniformMatrix4fv(program.u_matrix, false, this.matrix)

    gl.drawArrays(gl.TRIANGLES, 0, 6)
  }

  getPartcleNumber = () => {
    const gl = this.gl
    if (!gl || !gl.canvas) return this.maxParticles
    const area = gl.canvas.width * gl.canvas.height / Math.pow(this.dpr, 2)
    const density = isMobile ? 1 / 40 : 1 / 60
    const zoomFactor = 1.5 / this.map.getZoom()
    const num = Math.floor(area * density * zoomFactor)
    const number = Math.min(num, this.maxParticles)
    return number
  }

  /**
   * データを更新する関数
   * @params {Object} data
   * @params {Object.<Array>} data.data
   * @params {Object.<Number>} data.width
   * @params {Object.<Number>} data.height
   */
  updateData(data, GRID_INFO) {
    // GRID_INFOを更新
    this.GRID_INFO = structuredClone(GRID_INFO)
    this.GRID_INFO.LAT_DIFF = (GRID_INFO.NLAT - GRID_INFO.SLAT) / GRID_INFO.IMGH
    this.GRID_INFO.LON_DIFF = (GRID_INFO.ELON - GRID_INFO.WLON) / GRID_INFO.IMGW
    const projFunc = maplibregl.MercatorCoordinate.fromLngLat
    const mercEN = projFunc([GRID_INFO.ELON, GRID_INFO.NLAT])
    this.GRID_INFO.E_MERC_X = mercEN.x
    this.GRID_INFO.N_MERC_Y = mercEN.y
    const mercWS = projFunc([GRID_INFO.WLON, GRID_INFO.SLAT])
    this.GRID_INFO.W_MERC_X = mercWS.x
    this.GRID_INFO.S_MERC_Y = mercWS.y

    this.clear()
    if (this.clearId) {
      cancelAnimationFrame(this.clearId)
      this.clearId = null
    }
    this.resize()

    this.windData = { data, width: GRID_INFO.IMGW, height: GRID_INFO.IMGH }
    // 既存の windTexture があれば解放
    if (this.windTexture) {
      this.gl.deleteTexture(this.windTexture)
    }
    this.setWind(this.windData)
    this.anim()
  }

  /**
   * 描画用のキャンバスを作成する関数
   * @params { mapbox } map
   * @returns { HTMLCanvasElement }
   */
  createTargetCanvas(_map) {
    const canvas = document.createElement('canvas')
    canvas.classList.add('maplibregl-canvas')
    // map.getCanvasContainer().appendChild(canvas)
    // canvas.style.zIndex = 2
    return canvas
  }

  // 追加分: リソースをクリーンアップする関数
  cleanupResources() {
    const gl = this.gl
    if (this.backgroundTexture) {
      gl.deleteTexture(this.backgroundTexture)
      this.backgroundTexture = null
    }
    if (this.screenTexture) {
      gl.deleteTexture(this.screenTexture)
      this.screenTexture = null
    }
    if (this.framebuffer) {
      gl.deleteFramebuffer(this.framebuffer)
      this.framebuffer = null
    }
    if (this.particleStateBuffer0) {
      gl.deleteBuffer(this.particleStateBuffer0.buffer)
      this.particleStateBuffer0 = null
    }
    if (this.particleStateBuffer1) {
      gl.deleteBuffer(this.particleStateBuffer1.buffer)
      this.particleStateBuffer1 = null
    }
    // ...その他生成したリソースがあれば同様に解放...
  }

  async onAdd(map, _gl) {
    this.map = map
    this.canvas = this.createTargetCanvas(map)
    this.gl = this.canvas.getContext('webgl2')
    // this.gl = _gl

    this.instancedExt = this.gl.getExtension('ANGLE_instanced_arrays')
    this.gl.getExtension('EXT_color_buffer_float')

    await this.initProgram(this.gl)
    this.resize()

    this.createParticleTexture(this.getPartcleNumber())
    this.setWind(this.windData)

    // ループ多重化防止
    if (this.clearId) {
      cancelAnimationFrame(this.clearId)
      this.clearId = null
    }
    this.anim()

    map.on('movestart', () => {
      this.clear()
      if (this.clearId) {
        cancelAnimationFrame(this.clearId)
        this.clearId = null
      }
    })
    map.on('move', () => {
      this.clear()
      if (this.clearId) {
        cancelAnimationFrame(this.clearId)
        this.clearId = null
      }
    })
  }

  onRemove(_map, _gl) {
    this.clear()
    // 解放処理を一括で呼び出す
    this.cleanupResources()
    this.canvas.remove()
  }

  render(gl, args) {
    const matrix = args.defaultProjectionData.mainMatrix
    // Float32Array型で保証
    this.matrix = (matrix instanceof Float32Array) ? matrix : new Float32Array(matrix)
  }
}
